import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { SEOHead } from '../components/seo/SEOHead';
import { qrTemplates, categories, QRTemplate } from '../data/qrTemplates';
import { useQRStore } from '../store/useQRStore';
import { Search, Filter } from 'lucide-react';

export const TemplatesPage: React.FC = () => {
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const navigate = useNavigate();
  const { updateCustomization } = useQRStore();

  const filteredTemplates = qrTemplates.filter((template) => {
    const matchesCategory = selectedCategory === 'all' || template.category === selectedCategory;
    const matchesSearch = template.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      template.description.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const handleTemplateSelect = (template: QRTemplate) => {
    // Apply template customization
    if (template.customization) {
      updateCustomization(template.customization);
    }

    // Navigate to appropriate generator with pre-filled data
    const queryParams = new URLSearchParams();
    queryParams.set('type', template.type);
    
    // Add template fields as query params
    Object.entries(template.fields).forEach(([key, value]) => {
      queryParams.set(key, value);
    });

    navigate(`/generate?${queryParams.toString()}`);
  };

  return (
    <>
      <SEOHead
        title="QR Code Templates | FreeQRCodeNow"
        description="Browse professional QR code templates for business, personal, social media, marketing, and events. Create beautiful QR codes in seconds."
        keywords="qr code templates, qr code examples, qr code designs, business qr codes, social media qr codes"
      />

      <div className="space-y-8">
        {/* Header */}
        <div className="text-center">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            QR Code Templates
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Choose from our professionally designed templates to create stunning QR codes instantly.
            Perfect for business cards, social media, marketing campaigns, and more.
          </p>
        </div>

        {/* Search and Filter */}
        <Card>
          <CardContent className="p-6">
            <div className="flex flex-col md:flex-row gap-4">
              {/* Search */}
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search templates..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-input rounded-md bg-background"
                />
              </div>

              {/* Category Filter */}
              <div className="flex flex-wrap gap-2">
                <Button
                  variant={selectedCategory === 'all' ? 'primary' : 'outline'}
                  size="sm"
                  onClick={() => setSelectedCategory('all')}
                >
                  All Templates
                </Button>
                {categories.map((category) => (
                  <Button
                    key={category.id}
                    variant={selectedCategory === category.id ? 'primary' : 'outline'}
                    size="sm"
                    onClick={() => setSelectedCategory(category.id)}
                  >
                    <span className="mr-1">{category.icon}</span>
                    {category.name}
                  </Button>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Templates Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredTemplates.map((template) => (
            <Card key={template.id} className="hover:shadow-lg transition-shadow duration-300">
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <span className="text-4xl">{template.icon}</span>
                    <div>
                      <CardTitle className="text-lg">{template.name}</CardTitle>
                      <p className="text-xs text-muted-foreground capitalize">
                        {template.category}
                      </p>
                    </div>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-sm text-gray-600">{template.description}</p>

                {/* Preview Customization */}
                {template.customization && (
                  <div className="flex gap-2 items-center">
                    <span className="text-xs text-muted-foreground">Colors:</span>
                    {template.customization.foregroundColor && (
                      <div
                        className="w-6 h-6 rounded border border-gray-300"
                        style={{ backgroundColor: template.customization.foregroundColor }}
                        title="Foreground color"
                      />
                    )}
                    {template.customization.backgroundColor && (
                      <div
                        className="w-6 h-6 rounded border border-gray-300"
                        style={{ backgroundColor: template.customization.backgroundColor }}
                        title="Background color"
                      />
                    )}
                  </div>
                )}

                <Button
                  onClick={() => handleTemplateSelect(template)}
                  className="w-full"
                  size="sm"
                >
                  Use This Template
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>

        {filteredTemplates.length === 0 && (
          <Card className="p-12">
            <div className="text-center text-gray-500">
              <Filter className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p className="text-lg font-medium mb-2">No templates found</p>
              <p className="text-sm">Try adjusting your search or filter criteria</p>
            </div>
          </Card>
        )}

        {/* Call to Action */}
        <Card className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white">
          <CardContent className="p-8 text-center">
            <h2 className="text-2xl font-bold mb-3">
              Need a Custom QR Code?
            </h2>
            <p className="mb-6 opacity-90">
              Create your own custom QR code from scratch with full control over design and content
            </p>
            <Button
              variant="secondary"
              size="lg"
              onClick={() => navigate('/generate')}
              className="text-blue-600"
            >
              Create Custom QR Code
            </Button>
          </CardContent>
        </Card>
      </div>
    </>
  );
};


